import { useLazyQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useTRPCClient } from 'src/utils/trpc';

import { useErrorNotification } from '@/hooks/useErrorNotification';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type {
  LazyQueryFetchResult,
  LazyQueryHookConfig,
  LazyQueryHookFactoryResult,
  LazyQueryHookState,
  QueryHookError,
} from './types';

const isQueryHookError = (err: unknown): err is NonNullable<QueryHookError> => {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    typeof err.message === 'string'
  );
};

/**
 * Factory function that creates a lazy (on-demand) GraphQL-to-TRPC wrapper hook.
 *
 * Unlike createQueryHook which fetches eagerly on mount, this creates hooks that
 * return a callback function for imperative fetching. Useful for:
 * - Server-side table pagination/filtering
 * - Search-as-you-type
 * - Any scenario where data is fetched on demand rather than on mount
 *
 * The returned hook:
 * - Switches between GraphQL and TRPC based on the 'trpc' feature flag
 * - Returns a stable callback that accepts query args and returns a Promise
 * - Handles error notifications automatically
 * - Provides consistent return types (tuple of [fetchFn, { data, loading, error }])
 */
export const createLazyQueryHook = <
  TQueryArgs,
  TTRPCOutput,
  TGraphQLData extends { __typename?: 'query_root' },
>(
  config: LazyQueryHookConfig<TQueryArgs, TTRPCOutput, TGraphQLData>
): LazyQueryHookFactoryResult<TQueryArgs, TGraphQLData> => {
  const {
    trpcQuery,
    mapTrpcDataToGraphQL,
    graphqlDocument,
    graphqlVariables,
    graphqlFetchPolicy,
  } = config;

  return () => {
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpcClient = useTRPCClient();
    const [executeGraphQL, graphqlResult] = useLazyQuery(
      graphqlDocument,
      graphqlFetchPolicy ? { fetchPolicy: graphqlFetchPolicy } : undefined
    );

    const [trpcState, setTrpcState] = useState<
      LazyQueryHookState<TGraphQLData>
    >({
      data: undefined,
      loading: false,
      error: undefined,
    });

    const fetchWithTRPC = useCallback(
      async (args: TQueryArgs): Promise<LazyQueryFetchResult<TGraphQLData>> => {
        setTrpcState((prev) => ({ ...prev, loading: true, error: undefined }));
        try {
          const data = await trpcQuery(trpcClient, args);
          const mapped = mapTrpcDataToGraphQL(data);
          setTrpcState({ data: mapped, loading: false, error: undefined });

          return {
            data: mapped,
            loading: false,
            error: undefined,
          };
        } catch (err) {
          const error = isQueryHookError(err) ? err : undefined;
          setTrpcState((prev) => ({
            ...prev,
            loading: false,
            error,
          }));

          return {
            data: undefined,
            loading: false,
            error,
          };
        }
      },
      [trpcClient]
    );

    useErrorNotification(trpcState.error, trpcEnabled);

    const fetchWithGraphQL = useCallback(
      async (args: TQueryArgs): Promise<LazyQueryFetchResult<TGraphQLData>> => {
        const variables = graphqlVariables ? graphqlVariables(args) : args;
        const result = await executeGraphQL({ variables });

        return {
          data: result.data,
          loading: result.loading,
          error: result.error,
        };
      },
      [executeGraphQL]
    );

    useErrorNotification(graphqlResult.error, !trpcEnabled);

    if (trpcEnabled) {
      return [fetchWithTRPC, trpcState];
    }

    return [
      fetchWithGraphQL,
      {
        data: graphqlResult.data,
        loading: graphqlResult.loading,
        error: graphqlResult.error,
      },
    ];
  };
};
